// Week calendar component for manager team view
// CREATED: Expandable week calendar (1 week or month view)
// REFACTORED: Unified UX with consistent styling between collapsed/expanded modes
// UPDATED: Consistent chevron navigation (week/month), unified month display format, consistent styling
// Features: Dynamic height transitions, ARIA labels, improved contrast, fade animations, swipe support, out-of-month days clickable
// UPDATED: Desktop always shows month view (no collapse), mobile retains expand/collapse behavior

import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

interface WeekCalendarProps {
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  isDesktop?: boolean;
}

export function WeekCalendar({ selectedDate, onSelectDate, isDesktop = false }: WeekCalendarProps) {
  const [isExpanded, setIsExpanded] = useState(isDesktop);
  // Current month being displayed in expanded mode (year and month)
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    const date = new Date(selectedDate);
    date.setDate(1); // Set to first day of month
    return date;
  });
  const [isFading, setIsFading] = useState(false);
  const [calendarHeight, setCalendarHeight] = useState<number | undefined>(undefined);
  const calendarContentRef = useRef<HTMLDivElement>(null);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  const getWeekStart = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Monday as first day
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const generateWeeks = (): Date[][] => {
    if (isExpanded) {
      // In expanded mode: generate all weeks for the current month
      // A month can span 4, 5, or 6 weeks depending on the first day and month length
      const weeks: Date[][] = [];

      // Get the first day of the month
      const firstDayOfMonth = new Date(currentMonth);
      firstDayOfMonth.setDate(1);

      // Get the last day of the month
      const lastDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      // Get the Monday of the week containing the first day of the month
      const startDate = getWeekStart(firstDayOfMonth);

      // Get the Sunday of the week containing the last day of the month
      const endDate = new Date(lastDayOfMonth);
      const endDay = endDate.getDay();
      const daysToAdd = endDay === 0 ? 0 : 7 - endDay;
      endDate.setDate(endDate.getDate() + daysToAdd);

      // Generate weeks from start to end
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const week: Date[] = [];
        for (let i = 0; i < 7; i++) {
          week.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        weeks.push(week);
      }

      return weeks;
    } else {
      // In collapsed mode: show only the week containing the selected date
      const weekStart = getWeekStart(selectedDate);
      const week: Date[] = [];

      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const day = new Date(weekStart);
        day.setDate(day.getDate() + dayIndex);
        week.push(day);
      }

      return [week];
    }
  };

  // Navigation: Go to previous month with fade animation
  const goToPreviousMonth = useCallback(() => {
    setIsFading(true);
    setTimeout(() => {
      setCurrentMonth(prevMonth => {
        const newMonth = new Date(prevMonth);
        newMonth.setMonth(newMonth.getMonth() - 1);
        return newMonth;
      });
      setIsFading(false);
    }, 150);
  }, []);

  // Navigation: Go to next month with fade animation
  const goToNextMonth = useCallback(() => {
    setIsFading(true);
    setTimeout(() => {
      setCurrentMonth(prevMonth => {
        const newMonth = new Date(prevMonth);
        newMonth.setMonth(newMonth.getMonth() + 1);
        return newMonth;
      });
      setIsFading(false);
    }, 150);
  }, []);

  // Update currentMonth when expanding to show the month of the selected date
  useEffect(() => {
    if (isExpanded) {
      const monthOfSelectedDate = new Date(selectedDate);
      monthOfSelectedDate.setDate(1);
      setCurrentMonth(monthOfSelectedDate);
    }
  }, [isExpanded, selectedDate]);

  // Force expanded state on desktop
  useEffect(() => {
    if (isDesktop) {
      setIsExpanded(true);
    }
  }, [isDesktop]);

  // Get formatted month display - always show full month name with year
  const getMonthDisplay = (): string => {
    const dateToDisplay = isExpanded ? currentMonth : selectedDate;
    const monthName = dateToDisplay.toLocaleDateString('fr-FR', { month: 'long' });
    const year = dateToDisplay.getFullYear();
    return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${year}`;
  };

  // Jump to today's date and month
  const goToToday = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (isExpanded) {
      setIsFading(true);
      setTimeout(() => {
        // Update current month to today's month
        const todayMonth = new Date(today);
        todayMonth.setDate(1);
        setCurrentMonth(todayMonth);
        // Update selected date to today
        onSelectDate(today);
        setIsFading(false);
      }, 150);
    } else {
      // In collapsed mode, just select today
      onSelectDate(today);
    }
  }, [isExpanded, onSelectDate]);

  // Navigate to previous week (collapsed mode)
  const goToPreviousWeek = useCallback(() => {
    const prevWeek = new Date(selectedDate);
    prevWeek.setDate(prevWeek.getDate() - 7);
    onSelectDate(prevWeek);
  }, [selectedDate, onSelectDate]);

  // Navigate to next week (collapsed mode)
  const goToNextWeek = useCallback(() => {
    const nextWeek = new Date(selectedDate);
    nextWeek.setDate(nextWeek.getDate() + 7);
    onSelectDate(nextWeek);
  }, [selectedDate, onSelectDate]);

  const toggleExpanded = useCallback(() => {
    if (!isDesktop) {
      setIsExpanded(prev => !prev);
    }
  }, [isDesktop]);

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (date: Date): boolean => {
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  // Check if selected date is today
  const isSelectedDateToday = useCallback((): boolean => {
    const today = new Date();
    return (
      selectedDate.getDate() === today.getDate() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getFullYear() === today.getFullYear()
    );
  }, [selectedDate]);

  // Generate weeks - must be before useEffect that depends on it
  const weeks = generateWeeks();
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  // Calculate and update calendar height for smooth transitions
  useEffect(() => {
    if (calendarContentRef.current) {
      const height = calendarContentRef.current.scrollHeight;
      setCalendarHeight(height);
    }
  }, [isExpanded, weeks.length]);

  // Touch gesture handlers for swipe navigation
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null) return;

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const deltaX = touchStartXRef.current - touchEndX;
    const deltaY = touchStartYRef.current - touchEndY;

    // Only trigger swipe if horizontal movement is greater than vertical (avoid conflicts with scrolling)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX > 0) {
        // Swipe left - go to next
        if (isExpanded) {
          goToNextMonth();
        } else {
          goToNextWeek();
        }
      } else {
        // Swipe right - go to previous
        if (isExpanded) {
          goToPreviousMonth();
        } else {
          goToPreviousWeek();
        }
      }
    }

    touchStartXRef.current = null;
    touchStartYRef.current = null;
  }, [isExpanded, goToNextMonth, goToPreviousMonth, goToNextWeek, goToPreviousWeek]);

  // Handle click on out-of-month day
  const handleDayClick = useCallback((day: Date) => {
    // If clicking on a day outside current month in expanded mode, navigate to that month
    if (isExpanded && day.getMonth() !== currentMonth.getMonth()) {
      setIsFading(true);
      setTimeout(() => {
        const newMonth = new Date(day);
        newMonth.setDate(1);
        setCurrentMonth(newMonth);
        onSelectDate(day);
        setIsFading(false);
      }, 150);
    } else {
      onSelectDate(day);
    }
  }, [isExpanded, currentMonth, onSelectDate]);

  return (
    <div
      className="bg-white border border-gray-200 shadow-sm rounded-lg overflow-hidden transition-all duration-300"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role="region"
      aria-label="Calendrier de sélection de date"
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          {/* Previous navigation button - always visible */}
          <button
            onClick={isExpanded ? goToPreviousMonth : goToPreviousWeek}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors min-w-[44px] min-h-[44px] md:min-w-[36px] md:min-h-[36px] flex items-center justify-center"
            aria-label={isExpanded ? 'Mois précédent' : 'Semaine précédente'}
            type="button"
          >
            <ChevronLeft size={20} />
          </button>

          {/* Center section with Today button and Month display */}
          <div className="flex items-center gap-2">
            {/* Today button - only visible when not already on today */}
            {!isSelectedDateToday() && (
              <button
                onClick={goToToday}
                className="px-2.5 py-1.5 text-xs md:text-sm font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 rounded-lg transition-colors min-h-[36px] md:min-h-[40px] flex items-center active:scale-95"
                aria-label="Aller à aujourd'hui"
                type="button"
              >
                Aujourd'hui
              </button>
            )}

            {/* Month display with expand/collapse toggle (only clickable on mobile) */}
            <button
              onClick={toggleExpanded}
              className={`flex items-center gap-2 text-gray-900 transition-colors min-h-[44px] px-3 rounded-lg ${
                isDesktop ? 'cursor-default' : 'hover:bg-gray-100'
              }`}
              aria-label={isExpanded ? 'Réduire le calendrier' : 'Développer le calendrier'}
              aria-expanded={isExpanded}
              type="button"
              disabled={isDesktop}
            >
              <span className="text-base font-medium">{getMonthDisplay()}</span>
              {!isDesktop && (
                <ChevronDown
                  size={20}
                  className={`text-gray-500 transition-transform duration-300 ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                />
              )}
            </button>
          </div>

          {/* Next navigation button - always visible */}
          <button
            onClick={isExpanded ? goToNextMonth : goToNextWeek}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors min-w-[44px] min-h-[44px] md:min-w-[36px] md:min-h-[36px] flex items-center justify-center"
            aria-label={isExpanded ? 'Mois suivant' : 'Semaine suivante'}
            type="button"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Day names header */}
        <div className="grid grid-cols-7 gap-1 md:gap-1.5">
          {dayNames.map(name => (
            <div
              key={name}
              className="text-center text-xs font-medium text-gray-500"
            >
              {name}
            </div>
          ))}
        </div>
      </div>

      {/* Calendar grid */}
      <div
        className={`px-4 md:px-3 pb-4 md:pb-3 overflow-hidden transition-all duration-300 ease-in-out ${
          !isDesktop && isExpanded ? 'max-h-[50vh] overflow-y-auto' : ''
        }`}
        style={{
          maxHeight: !isDesktop && isExpanded ? '50vh' : (calendarHeight ? `${calendarHeight}px` : 'none')
        }}
      >
        <div
          ref={calendarContentRef}
          className={`pt-2 space-y-2 transition-opacity duration-150 ${
            isFading ? 'opacity-0' : 'opacity-100'
          }`}
        >
          {weeks.map((week, weekIndex) => {
            return (
              <div key={weekIndex}>
                <div className="grid grid-cols-7 gap-1 md:gap-1.5">
                  {week.map((day, dayIndex) => {
                    const today = isToday(day);
                    const selected = isSelected(day);
                    // Dim days that are not in the current month (expanded mode only)
                    const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                    const isDimmed = isExpanded && !isCurrentMonth;

                    const dateString = day.toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    });

                    return (
                      <button
                        key={dayIndex}
                        onClick={() => handleDayClick(day)}
                        className={`relative min-h-[44px] min-w-[44px] md:min-h-[36px] md:min-w-[36px] flex flex-col items-center justify-center transition-colors duration-150 ease-out rounded-lg text-sm font-medium ${
                          selected
                            ? 'bg-primary-600 text-white font-semibold shadow-md ring-2 ring-primary-200 ring-offset-1'
                            : today
                            ? 'text-primary-700 font-semibold'
                            : isDimmed
                            ? 'text-gray-400 hover:bg-gray-50 hover:text-gray-700 active:bg-gray-100'
                            : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                        }`}
                        aria-label={selected ? `${dateString} (sélectionné)` : today ? `${dateString} (aujourd'hui)` : dateString}
                        aria-pressed={selected}
                        type="button"
                      >
                        <span>{day.getDate()}</span>
                        {/* Today indicator - discrete dot */}
                        {today && (
                          <span
                            className={`absolute bottom-1 w-1.5 h-1.5 rounded-full ${
                              selected ? 'bg-white' : 'bg-primary-500'
                            }`}
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
